import { createContext } from "react";
import { useAppSelector } from "../../app/hooks";
import {
  canExpertAddContract,
  canExpertAddCustomerCompany,
  canExpertAddCustomerOrganization,
  canExpertDeletePayslips,
  canExpertEditAccumulations,
  canExpertEditAssignment,
  canExpertEditContract,
  canExpertEditContractValidFrom,
  canExpertEditCustomer,
  canExpertEditEmployment,
  canExpertEditEntries,
  canExpertEditManualSalaries,
  canExpertEditOrderTemplate,
  canExpertEditTalentTaxNumber,
  canExpertEditTalentProfile,
  canExpertEditTalentSalaryInfo,
  canExpertEditUnionPayment,
  canExpertEditWithholdingTaxCorrection,
  canExpertModifyPayslips,
  canExpertReadAssigmentPricingInfo,
  canExpertReadContract,
  canExpertReadEmployment,
  canExpertReadInvoices,
  canExpertReadOrder,
  canExpertReadUnionPayment,
  canExpertWriteJobAd,
  isExpertAdmin,
  isExpertSalaryBillingHr,
} from "./accessRightChecks";

interface AccessRightContextValues {
  canEditTalentSalaryInfo: boolean;
  canEditTalentTaxNumber: boolean;
  canEditTalentProfile: boolean;
  canEditEmployment: (eezyCompanyId: string) => boolean;
  canEditAssignment: (eezyCompanyId: string) => boolean;
  canReadAssignmentPricingInfo: (eezyCompanyId: string) => boolean;
  canReadUnionPayment: boolean;
  canEditUnionPayment: boolean;
  canAddCustomerCompany: boolean;
  canAddCustomerOrganization: boolean;
  canEditCustomer: boolean;
  canEditOrderTemplate: (eezyCompanyId: string) => boolean;
  canAddContract: boolean;
  canEditContract: (eezyCompanyId: string) => boolean;
  canEditContractValidFrom: (eezyCompanyId: string) => boolean;
  canReadContract: (eezyCompanyId: string) => boolean;
  canEditEntries: boolean;
  canEditPayslips: boolean;
  canDeletePayslips: boolean;
  canEditManualSalaries: boolean;
  canEditWithholdingTaxCorrection: boolean;
  canWriteJobAd: boolean;
  canReadOrder: (eezyCompanyId: string) => boolean;
  canEditAccumulations: boolean;
  isAdmin: boolean;
  isSalaryBillingHr: boolean;
  canReadInvoices: boolean;
  canReadEmployment: (eezyCompanyId: string) => boolean;
}

// @ts-ignore
export const AccessRightContext = createContext<AccessRightContextValues>();

export const AccessRightProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const user = useAppSelector((state) => state.user.user);

  const canEditTalentSalaryInfo =
    (user && canExpertEditTalentSalaryInfo(user)) || false;
  const canEditTalentTaxNumber =
    (user && canExpertEditTalentTaxNumber(user)) || false;
  const canEditTalentProfile =
    (user && canExpertEditTalentProfile(user)) || false;
  const canEditEmployment = (eezyCompanyId: string) =>
    (user && canExpertEditEmployment(user, eezyCompanyId)) || false;
  const canEditAssignment = (eezyCompanyId: string) =>
    (user && canExpertEditAssignment(user, eezyCompanyId)) || false;
  const canReadAssignmentPricingInfo = (eezyCompanyId: string) =>
    (user && canExpertReadAssigmentPricingInfo(user, eezyCompanyId)) || false;
  const canReadUnionPayment =
    (user && canExpertReadUnionPayment(user)) || false;
  const canEditUnionPayment =
    (user && canExpertEditUnionPayment(user)) || false;
  const canAddCustomerCompany =
    (user && canExpertAddCustomerCompany(user)) || false;
  const canAddCustomerOrganization =
    (user && canExpertAddCustomerOrganization(user)) || false;
  const canEditCustomer = (user && canExpertEditCustomer(user)) || false;
  const canEditOrderTemplate = (eezyCompanyId: string) =>
    (user && canExpertEditOrderTemplate(user, eezyCompanyId)) || false;
  const canAddContract = (user && canExpertAddContract(user)) || false;
  const canEditContract = (eezyCompanyId: string) =>
    (user && canExpertEditContract(user, eezyCompanyId)) || false;
  const canEditContractValidFrom = (eezyCompanyId: string) =>
    (user && canExpertEditContractValidFrom(user, eezyCompanyId)) || false;
  const canReadContract = (eezyCompanyId: string) =>
    (user && canExpertReadContract(user, eezyCompanyId)) || false;
  const canEditEntries = (user && canExpertEditEntries(user)) || false;
  const canEditPayslips = (user && canExpertModifyPayslips(user)) || false;
  const canDeletePayslips = (user && canExpertDeletePayslips(user)) || false;
  const canReadEmployment = (eezyCompanyId: string) =>
    (user && canExpertReadEmployment(user, eezyCompanyId)) || false;
  const canEditManualSalaries =
    (user && canExpertEditManualSalaries(user)) || false;
  const canEditWithholdingTaxCorrection =
    (user && canExpertEditWithholdingTaxCorrection(user)) || false;
  const canReadOrder = (eezyCompanyId: string) =>
    (user && canExpertReadOrder(user, eezyCompanyId)) || false;
  const canWriteJobAd = (user && canExpertWriteJobAd(user)) || false;
  const canReadInvoices = (user && canExpertReadInvoices(user)) || false;
  const canEditAccumulations =
    (user && canExpertEditAccumulations(user)) || false;
  const isAdmin = (user && isExpertAdmin(user)) || false;
  const isSalaryBillingHr = (user && isExpertSalaryBillingHr(user)) || false;

  return (
    <AccessRightContext.Provider
      value={{
        canEditTalentSalaryInfo,
        canEditTalentTaxNumber,
        canEditTalentProfile,
        canEditEmployment,
        canReadUnionPayment,
        canEditUnionPayment,
        canAddCustomerCompany,
        canAddCustomerOrganization,
        canEditCustomer,
        canEditOrderTemplate,
        canAddContract,
        canEditContract,
        canEditContractValidFrom,
        canReadContract,
        canEditAssignment,
        canReadAssignmentPricingInfo,
        canEditEntries,
        canEditPayslips,
        canDeletePayslips,
        canEditManualSalaries,
        canEditWithholdingTaxCorrection,
        canReadOrder,
        canEditAccumulations,
        isAdmin,
        isSalaryBillingHr,
        canReadEmployment,
        canWriteJobAd,
        canReadInvoices,
      }}
    >
      {children}
    </AccessRightContext.Provider>
  );
};
